// Путь сделки: правила показа цепочки «заявка → … → оплата» и её разрывов.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Разметка живёт в pages/DealChain.tsx, а здесь — то, что должно
// проверяться без браузера: как называется каждое состояние звена, какое из них считается
// ответом на «где встало», и как одной строкой сказать человеку, что именно не движется.
//
// ЧЕГО ЗДЕСЬ НЕТ И НЕ БУДЕТ — НИ ОДНОГО СВОЕГО ПРАВИЛА ПРО ДАННЫЕ.
// Ни «оплачен ли счёт», ни «есть ли разрыв», ни «где встало» здесь не вычисляется. Всё это
// считает ядро (chain_api.py) ОДНИМ SQL для карточки, счётчика и списка. Стоит завести здесь
// вторую такую формулу — и однажды экран скажет «всё хорошо» про сделку, которая стоит
// в списке разрывов, а ответить на «какому экрану верить» будет нечем.
//
// Здесь только перевод машинных кодов в человеческие слова и порядок показа.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО ЭКРАНА: прочерк, ноль и «не знаю» — ТРИ РАЗНЫХ ОТВЕТА.
// У сделки 2018 года нет АВР не потому, что работу не сдали, а потому, что смарт-процесс
// АВР начали вести 20.02.2024. Ядро отдаёт это состоянием «не_знаем», и экран обязан
// написать словами, почему он молчит, а не показать пустую строку.

import { источник } from '../api/istochnik'
import { fmtMoney, fmtText } from './format'

/** База ручек ядра через BFF. */
export const БАЗА = '/api/chain'

// Источник (своя схема или зеркало Bitrix) уходит В КАЖДЫЙ запрос явным ?src=:
// chain_api.py двухисточниковый (по образцу docs_api), его серверное умолчание —
// mirror, и без явного параметра экран не следовал бы общему переключателю CRM
// (api/istochnik.ts). Явный src в обе стороны — ещё и след в логах ядра: по одним
// цифрам источник не отличить, а сверять ветки приходится каждый день.
export const ПУТИ = {
  справочники: (дней?: number) =>
    `/refs${строкаЗапроса({ days: дней || undefined, src: источник() })}`,
  сделка: (id: string | number) =>
    `/deal${строкаЗапроса({ id: String(id), src: источник() })}`,
  разрывы: (ф: ФильтрыРазрывов) => `/gaps${строкаЗапроса({ ...ф, src: источник() })}`,
}

/**
 * Строка запроса без пустых параметров. Пустой фильтр в адресе — не косметика: ядро
 * отвечает 400 на `kind=`, и экран показал бы отказ там, где человек ничего не выбирал.
 */
export function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ═══════════════════════════ форма ответа ядра ═══════════════════════════
// Повторяет chain_api.py. Поля-подробности необязательные намеренно: у каждого типа записи
// свои, и у чужого типа их просто нет. Придумывать «пустое значение» вместо отсутствующего
// поля значило бы стереть разницу между «ноль» и «такого поля не бывает».

export type СостояниеЗвена = 'есть' | 'нет' | 'не_знаем' | 'не_сведено'
export type ВажностьРазрыва = 'разрыв' | 'наблюдение'

export interface ЗаписьЗвена {
  id?: number | null
  type?: number | null
  title?: string | null
  stage_id?: string | null
  stage_code?: string | null
  category_id?: number | string | null
  assigned_by_id?: number | null
  created?: string | null
  updated?: string | null
  /** Найдена по parentId2 (true) или по своему полю-привязке (false). */
  by_parent?: boolean
  sum?: number | null
  currency?: string | null
  /** День, которым запись стоит на ленте времени. */
  at?: string | null
  /** Этап закончен: счёт оплачен, АВР оформлен, отчёт согласован. */
  done?: boolean | null
  /* счёт клиенту */
  issued?: string | null
  due?: string | null
  number?: string | null
  /* платёж */
  sum_from?: string
  paid?: string | null
  received?: number | null
  shipped?: string | null
  upd?: string | null
  upd_sum?: number | null
  invoice_number?: string | null
  deal_field?: string | null
  /* АВР */
  act_date?: string | null
  invoice_sum?: number | null
  our_invoice?: string | null
  /* договор */
  doc_date?: string | null
  signed?: string | null
  /* отчёт по сделке */
  begin?: string | null
  end?: string | null
  waiting?: boolean
  /* заявка на оплату поставщику */
  plan?: string | null
  /* отгрузка */
  payment_id?: number | null
  /* верификация и отчёт портала 73 */
  status?: string | null
  fill_pct?: number | null
  defects?: number | null
  submitted?: string | null
  reviewed?: string | null
  /* документы 1С УТ (звено «Реализация в 1С» и блок docs_1c).
     Ссылки, а не названия: в зеркале документов лежат только guid'ы, справочники 1С
     в Postgres не переносились. Имена подставляет экран из тех же справочников через
     прокси, что и раздел «Отгрузка». */
  ref?: string | null
  kind?: string | null
  posted?: boolean
  settlement?: number | null
  currency_ref?: string | null
  warehouse_ref?: string | null
  client_ref?: string | null
  manager_ref?: string | null
  address?: string | null
  order_ref?: string | null
  order_number?: string | null
  /** У заказа клиента — плановая дата отгрузки, у заказа поставщику — дата поступления. */
  plan_date?: string | null
  /* акты на оборудование */
  act_type?: string | null
  place?: string | null
  units?: number | null
  vessel_name?: string | null
  engineer?: string | null
  /* состав сделки (единственная «запись» звена состава) */
  scanned?: boolean
  lines?: number
  delta?: number | null
  mismatch?: boolean
  empty_with_sum?: boolean
  outdated?: boolean
  checked_at?: string | null
}

export interface Звено {
  key: string
  title: string
  source: string
  link: string
  state: СостояниеЗвена
  count: number
  items: ЗаписьЗвена[]
  /** День, которым звено встаёт на ленту. null — датировать нечем. */
  at: string | null
  /** С какого дня этот учёт вообще ведут. null — у звена нет границы (сама сделка). */
  since?: string | null
  /** Последняя запись этого типа в зеркале. */
  last?: string | null
  /** Сколько всего записей этого типа и на сколько сделок они приходятся (только в /refs). */
  rows?: number | null
  deals?: number | null
  entity_type_id?: number
  note?: string | null
  /** Только у звеньев «рядом»: всего найдено, из них показано count. */
  total?: number | null
  why?: string
  /**
   * Только у звена «Реализация в 1С»: заказы клиента этой сделки. Не items, потому что
   * items звена — это реализации, а заказ не реализация: именно на разнице между ними и
   * стоит ответ «заказ есть, отгрузки нет». Показывать их вперемешку значило бы стереть
   * ровно тот факт, ради которого звено появилось.
   */
  orders?: ЗаписьЗвена[]
}

export interface Разрыв {
  code: string
  title: string
  why: string
  severity: ВажностьРазрыва
  step: string
  /** Только в /chain/refs: сколько таких сделок в окне. У own-ветки НЕВЫЧИСЛИМЫЙ вид
   *  приходит с n=null и computable=false — это «судить нечем», а не ноль. */
  n?: number | null
  computable?: boolean
}

export interface ГдеВстало {
  step: string
  code: string
  title: string
}

export interface ПутьСделки {
  deal: {
    id: number
    title: string | null
    stage_id: string | null
    stage_code: string | null
    category_id: number | null
    opportunity: number | null
    currency: string
    closed: boolean | null
    assigned_by_id: number | null
    unit_id: string | null
    date_create: string | null
    begindate: string | null
    closedate: string | null
    date_modify: string | null
    company_id: number | null
    company_title: string | null
    contact_id: number | null
    day: string | null
  }
  vessel: {
    id: number
    name: string | null
    imo: string | null
    flag: string | null
    /** Сколько карточек судна с этим IMO. Больше одной — акты могут быть от другой. */
    imo_rows: number
  } | null
  steps: Звено[]
  nearby: Звено[]
  /**
   * Документы 1С этой сделки целиком. Заказы клиента лежат отдельно от реализаций
   * намеренно: «заказ есть, реализации нет» — это утверждение, и заказ здесь его
   * доказательство. `since` — с какого дня в зеркале вообще есть реализации: раньше
   * этого дня пустое звено не значит ничего.
   */
  docs_1c: {
    orders: ЗаписьЗвена[]
    sales: ЗаписьЗвена[]
    supply: ЗаписьЗвена[]
    since: string | null
    supply_link: string
  }
  p73_requests: Array<{
    id: number; title: string | null; status: string | null; location: string | null
    created: string | null; closed: string | null; work_amount: number | null
  }>
  products: ЗаписьЗвена
  due_moves: Array<{
    payment_id: number; old: string | null; new: string | null
    changed_at: string | null; after_due: boolean
  }>
  gaps: Разрыв[]
  stuck_at: ГдеВстало | null
  window: { hang_days: number; today: string; acts_limit: number }
  data_notes: Record<string, string>
}

export interface СтрокаРазрыва {
  deal_id: number
  title: string | null
  opportunity: number | null
  currency: string | null
  stage_id: string | null
  stage_code: string | null
  category_id: number | null
  assigned_by_id: number | null
  company_id: number | null
  unit_id: string | null
  closed: boolean | null
  closedate: string | null
  date_create: string | null
  date_modify: string | null
  n_счёт: number
  n_счёт_опл: number
  n_авр: number
  n_плат: number
  n_плат_опл: number
  n_не_отгр: number
  n_просрочен: number
  n_строк: number
  состав_дельта: number | null
  gaps: string[]
  stuck_at: ГдеВстало | null
}

export interface СписокРазрывов {
  rows: СтрокаРазрыва[]
  total: number
  next: number | null
  limit: number
  start: number
  kind: string | null
  window: { days: number | null; won_only: boolean; hang_days: number; today: string }
  kinds: Разрыв[]
  data_notes: Record<string, string>
}

export interface СправочникиПути {
  steps: Звено[]
  nearby: Звено[]
  gap_kinds: Разрыв[]
  window: { days: number | null; deals: number; hang_days: number; today: string }
  synced: Array<{ entity: string; last_sync_at: string | null; last_full: string | null
                  last_count: number | null; age_min: number | null }>
  counts: Record<string, number>
  data_notes: Record<string, string>
}

export interface ФильтрыРазрывов {
  kind?: string
  days?: number
  category?: number | string
  assigned?: number | string
  /**
   * id компании Bitrix. Появился в ядре 28.07.2026: по одному клиенту ответ занимает
   * 0,06 с против 0,76 с по всей базе (замер на боевом ядре 91.186.198.176).
   *
   * ВАЖНО ПРО ПРАВА. Дверь BFF на `/api/chain/gaps` — `rights.проверить_чтение_свода`, и
   * этого параметра она НЕ ВИДИТ: роль со своей зоной получит 403 независимо от него.
   * Значит спрашивать с ним имеет смысл только там, где человеку видна вся база, а на
   * месте блока у остальных должно стоять объяснение отказа — пустой блок читался бы
   * как «ничего не встало».
   */
  company?: number | string
  won?: boolean
  start?: number
  limit?: number
}

export const РАЗМЕР_СТРАНИЦЫ = 50
/** Окна, которые предлагает экран. Совпадают с ОКНО_ДНЕЙ в ядре по умолчанию (90). */
export const ОКНА: Array<{ дней: number; ярлык: string }> = [
  { дней: 30, ярлык: 'месяц' },
  { дней: 90, ярлык: 'квартал' },
  { дней: 365, ярлык: 'год' },
]

// ═══════════════════════════ состояния звена ═══════════════════════════

/**
 * Четыре состояния и что каждое РЕАЛЬНО означает. Тексты здесь, а не в разметке, потому что
 * именно они отличают честный экран от вранья: «нет» и «не знаем» выглядят одинаково пусто,
 * а значат противоположное.
 *
 * `класс` — только из дизайн-системы (.badge won/lose/work). Своих цветов не вводим.
 */
export const СОСТОЯНИЯ: Record<СостояниеЗвена, {
  ярлык: string; класс: string; смысл: string
}> = {
  'есть': {
    ярлык: 'есть',
    класс: 'badge won',
    смысл: 'записи по этому этапу найдены',
  },
  'нет': {
    ярлык: 'нет',
    класс: 'badge lose',
    смысл: 'привязка проверена, записей нет. Этап не сделан или не оформлен — это и есть '
      + 'разрыв цепочки',
  },
  'не_знаем': {
    ярлык: 'не знаем',
    класс: 'badge work',
    смысл: 'ответить нечем: такой учёт в это время ещё не вели (или источник про эту сделку '
      + 'не знает). Отсутствие записи НЕ означает, что работы не было',
  },
  'не_сведено': {
    ярлык: 'не сведено',
    класс: 'badge work',
    смысл: 'связи в ядре нет вовсе — данные лежат там, откуда их к сделке не привязать',
  },
}

export const состояние = (s?: string | null) =>
  СОСТОЯНИЯ[(s ?? '') as СостояниеЗвена] ?? {
    ярлык: fmtText(s), класс: 'badge work',
    смысл: 'состояние, которого этот экран пока не знает — показано как пришло из ядра',
  }

/** Класс ярлыка для важности разрыва. Наблюдение — нейтральное, чтобы не спорило с разрывом. */
export const классВажности = (v?: string | null) =>
  v === 'разрыв' ? 'badge lose' : 'badge work'

/**
 * Состояние, из-за которого этап стоит показать крупно. Только 'нет': остальные три либо
 * норма ('есть'), либо честное молчание — а молчание красным не помечают.
 */
export const этоРазрыв = (з: Звено) => з.state === 'нет'

// ═══════════════════════════ ответ в одну строку ═══════════════════════════

export interface Итог {
  /** Что показать крупно. */
  заголовок: string
  /** Почему так — словами ядра, если разрыв нашёлся. */
  пояснение: string
  /** Класс ярлыка для заголовка. */
  класс: string
  /** Ключ звена, на котором встало. Пусто — не встало или судить нечем. */
  звено: string
}

/**
 * Главный ответ экрана: «где эта работа встала». Одна строка, которую человек читает за
 * пять секунд, — ради неё весь раздел и существует.
 *
 * Разбор ведётся ТОЛЬКО по тому, что уже посчитало ядро (stuck_at и gaps): ни одного своего
 * условия. Три возможных ответа, и третий не менее важен, чем первые два:
 *   • встало на звене X — есть разрыв;
 *   • цепочка прошла — разрывов нет, а звенья с записями есть;
 *   • судить нечем — все звенья в состоянии «не знаем» (сделка старше учёта).
 */
export function итогПути(п?: ПутьСделки): Итог {
  if (!п) {
    return { заголовок: '—', пояснение: '', класс: 'badge work', звено: '' }
  }
  if (п.stuck_at) {
    const разрыв = п.gaps.find((g) => g.code === п.stuck_at?.code)
    const звено = п.steps.find((s) => s.key === п.stuck_at?.step)
    return {
      заголовок: `Встало: ${звено ? звено.title.toLowerCase() : п.stuck_at.step}`,
      пояснение: разрыв ? `${разрыв.title}. ${разрыв.why}` : п.stuck_at.title,
      класс: 'badge lose',
      звено: п.stuck_at.step,
    }
  }
  // Ни одного разрыва. Но «прошла» и «сказать нечего» — разные вещи: у сделки 2018 года
  // все звенья в «не знаем», и объявлять её цепочку пройденной было бы ложью.
  const сЗаписями = п.steps.filter((s) => s.state === 'есть')
  const неЗнаем = п.steps.filter((s) => s.state === 'не_знаем')
  if (сЗаписями.length <= 1 && неЗнаем.length > 0) {
    return {
      заголовок: 'Судить нечем',
      пояснение: 'почти вся цепочка в состоянии «не знаем»: этой сделки не касался ни один '
        + 'из учётов, по которым мы судим о ходе работы. Чаще всего это сделка старше самих '
        + 'смарт-процессов — счета ведут с 2023 года, АВР и платежи с 2024',
      класс: 'badge work',
      звено: '',
    }
  }
  const наблюдения = п.gaps.filter((g) => g.severity === 'наблюдение')
  return {
    заголовок: 'Цепочка прошла',
    пояснение: наблюдения.length
      ? `Разрывов нет. Есть что заметить: ${наблюдения.map((g) => g.title.toLowerCase()).join('; ')}`
      : 'Разрывов нет: каждый этап, по которому мы можем судить, оформлен',
    класс: 'badge won',
    звено: '',
  }
}

// ═══════════════════════════ короткая суть звена ═══════════════════════════

/**
 * Что показать в строке звена одной фразой. Строится из полей, которые у этого типа
 * действительно есть; чего нет — не упоминается вовсе (а не пишется «—»): перечень
 * прочерков занимает место и ничего не сообщает.
 */
export function сутьЗаписи(ключ: string, r: ЗаписьЗвена): string {
  const части: string[] = []
  const деньги = (v?: number | null, вал?: string | null) =>
    (v === null || v === undefined ? '' : fmtMoney(v, вал || 'RUB'))

  switch (ключ) {
    case 'deal':
      if (r.sum) части.push(деньги(r.sum, r.currency))
      break
    case 'products':
      части.push(`${r.lines ?? 0} ${плюрал(r.lines ?? 0, 'позиция', 'позиции', 'позиций')}`)
      if (r.sum !== null && r.sum !== undefined) части.push(`на ${деньги(r.sum)}`)
      break
    case 'contract':
      if (r.number) части.push(`№ ${r.number}`)
      if (r.doc_date) части.push(`от ${r.doc_date}`)
      if (r.signed) части.push(`подписан: ${r.signed}`)
      break
    case 'verification':
      if (r.status) части.push(r.status)
      if (r.fill_pct !== null && r.fill_pct !== undefined) части.push(`заполнено ${r.fill_pct} %`)
      if (r.defects) части.push(`дефектов: ${r.defects}`)
      break
    case 'job_report':
      if (r.status) части.push(r.status)
      if (r.fill_pct !== null && r.fill_pct !== undefined) части.push(`заполнено ${r.fill_pct} %`)
      break
    case 'avr':
      if (r.number) части.push(`АВР № ${r.number}`)
      if (r.act_date) части.push(`акт от ${r.act_date}`)
      if (r.invoice_sum) части.push(`по счёту ${деньги(r.invoice_sum)}`)
      break
    case 'report':
      if (r.end) части.push(`завершён ${r.end}`)
      else if (r.begin) части.push(`начат ${r.begin}`)
      if (r.waiting) части.push('ждёт согласования')
      break
    case 'invoice':
      if (r.number) части.push(`счёт № ${r.number}`)
      if (r.sum) части.push(деньги(r.sum, r.currency))
      if (r.issued) части.push(`выставлен ${r.issued}`)
      if (r.due) части.push(`срок ${r.due}`)
      break
    case 'payment':
      if (r.sum) части.push(деньги(r.sum, r.currency))
      if (r.paid) части.push(`оплачен ${r.paid}`)
      else if (r.due) части.push(`срок ${r.due}`)
      if (r.invoice_number) части.push(`счёт ${r.invoice_number}`)
      break
    case 'shipment':
      if (r.shipped) части.push(`отгружен ${r.shipped}`)
      if (r.upd) части.push(`УПД № ${r.upd}`)
      break
    case 'supplier_bill':
      if (r.number) части.push(`счёт поставщика № ${r.number}`)
      if (r.sum) части.push(деньги(r.sum, r.currency))
      if (r.paid) части.push(`оплачен ${r.paid}`)
      else if (r.plan) части.push(`план ${r.plan}`)
      break
    // Документ 1С. Номер и сумма — то, по чему кладовщик найдёт его у себя. Пометка
    // «не проведён» важнее суммы: непроведённый документ движения по складу не делает.
    case 'realization_1c':
      if (r.number) части.push(`№ ${r.number}`)
      if (r.sum) части.push(деньги(r.sum))
      if (r.order_number) части.push(`по заказу ${r.order_number}`)
      else if (r.kind === 'sale') части.push('не «на основании» заказа')
      if (r.posted === false) части.push('НЕ проведён')
      if (r.address) части.push(r.address)
      break
    case 'equip_acts':
      if (r.number) части.push(`№ ${r.number}`)
      if (r.act_date) части.push(String(r.act_date).slice(0, 10))
      if (r.units) части.push(`${r.units} ед.`)
      if (r.engineer) части.push(r.engineer)
      break
    default:
      break
  }
  return части.join(' · ')
}

/**
 * Множественное число по-русски. Своя копия здесь не нужна была бы, будь она в lib/format,
 * но там её нет, а в lib/dashboard она лежит вместе с правилами кабинета руководителя —
 * тянуть оттуда весь модуль ради одной функции неправильно. Как только `plural` переедет
 * в lib/format, эта копия удаляется.
 */
export function плюрал(n: number, одна: string, две: string, много: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return много
  if (b > 1 && b < 5) return две
  if (b === 1) return одна
  return много
}

/**
 * Подпись про расхождение состава. Отдельно от `сутьЗаписи`, потому что это не описание
 * записи, а вывод, и он должен быть виден рядом с суммой сделки, а не в строке состава.
 * Само расхождение считает ядро — здесь только слова.
 */
export function оСоставе(п?: ПутьСделки): string {
  const c = п?.products
  if (!c) return ''
  if (!c.scanned) {
    return 'состав этой сделки ещё не опрашивали: «позиций нет» сказать нельзя, это разные вещи'
  }
  if (c.mismatch) {
    const знак = (c.delta ?? 0) > 0 ? 'дороже' : 'дешевле'
    return `состав ${знак} суммы сделки на ${fmtMoney(Math.abs(c.delta ?? 0))}`
      + ` (${fmtMoney(c.sum ?? 0)} против ${fmtMoney(п?.deal.opportunity ?? 0)})`
  }
  if (c.empty_with_sum) {
    return 'сумма у сделки стоит, а состава нет ни одной позиции — сравнивать не с чем'
  }
  if (c.outdated) {
    return 'сделку правили после того, как опрашивали состав: сверка могла устареть'
  }
  return ''
}

/** Переносы срока, сделанные ПОСЛЕ его наступления. Именно они прячут просрочку. */
export const переносыЗадним = (п?: ПутьСделки) =>
  (п?.due_moves ?? []).filter((m) => m.after_due)

/**
 * Виды разрывов, отсортированные по числу сделок вниз. Ответ на «где чаще всего рвётся» —
 * его считает ядро, здесь только порядок. Наблюдения уходят в конец: их всегда больше,
 * и наверху они вытеснили бы настоящие разрывы.
 */
export function видыПоЧастоте(виды: Разрыв[]): Разрыв[] {
  return [...виды].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'разрыв' ? -1 : 1
    return (b.n ?? 0) - (a.n ?? 0)
  })
}
